import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo } from 'react';
import { Stack } from '@mui/material';

import { MetricCardProps } from '@occc/ui-design-system';
import { DateTimeFormat, formatDateTime, parseDate } from '@occc-app/lib/date';
import { RetailerNetworkTobaccoProgramMetricType } from '@occc/graphql';
import { hasAnyNonZeroProps } from '@occc/utils';

import { RetailerNetworkTobaccoPerformance } from './retailer-network-tobacco-performance.component';
// NOTE: RetailerNetworkTobaccoPerformanceManufacturers (the "Offers by Manufacturer" section) is
// intentionally hidden per OCST-33444. The component, its hooks, and GraphQL documents are retained
// in the repo for a future redesign — do not delete them. To reinstate, restore this import, the
// useRetailerNetworkCpgManufacturerPrograms call, and the <RetailerNetworkTobaccoPerformanceManufacturers>
// element below, and re-add the manufacturer-programs query to the loading/error wrapper.

import { RetailerNetworkDataLoading } from '../../components/retailer-network-data-loading.component';
import { routeCreator } from '../../retailer-connect.utils';
import { useRetailerNetworkTobaccoProgramMetricsOverview } from '../../hooks/use-retailer-network-tobacco-program-metrics-overview.hook';
import { RetailerNetworkDataPlaceholder } from '../../components/retailer-network-data-placeholder.component';
import { tobaccoEmptyDataDescription, tobaccoEmptyDataTitle } from '../../retailer-connect.copies';

type RetailerNetworkTobaccoPerformanceControllerProps = {
  retailerNetworkUuid: string;
  startDate: Date;
  endDate: Date;
  onZeroData?: () => void;
};

export const RetailerNetworkTobaccoPerformanceController = ({
  retailerNetworkUuid,
  startDate,
  endDate,
  onZeroData,
}: RetailerNetworkTobaccoPerformanceControllerProps) => {
  const navigate = useNavigate();

  const { data, error, loading } = useRetailerNetworkTobaccoProgramMetricsOverview({
    retailerNetworkUuidEquals: retailerNetworkUuid,
    dateFrom: formatDateTime(startDate, DateTimeFormat.DateDashed),
    dateTo: formatDateTime(endDate, DateTimeFormat.DateDashed),
  });

  const onMetricClick = useCallback(
    (metricCard: MetricCardProps<RetailerNetworkTobaccoProgramMetricType>) => {
      navigate(
        routeCreator.tobaccoPerformanceDetails(retailerNetworkUuid, {
          metricKey: metricCard.payload,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        }),
      );
    },
    [navigate, retailerNetworkUuid, endDate, startDate],
  );

  const metrics = useMemo(() => {
    return (
      data?.retailerNetworkTobaccoProgramMetricsOverview?.tobaccoTransactionsTimeSeries.map(
        ({ date, value }) => ({
          date: parseDate(date),
          value,
        }),
      ) ?? []
    );
  }, [data?.retailerNetworkTobaccoProgramMetricsOverview?.tobaccoTransactionsTimeSeries]);

  const isZeroData = useMemo(
    () =>
      data?.retailerNetworkTobaccoProgramMetricsOverview &&
      !hasAnyNonZeroProps(data.retailerNetworkTobaccoProgramMetricsOverview),
    [data?.retailerNetworkTobaccoProgramMetricsOverview],
  );

  useEffect(() => {
    if (!isZeroData || !onZeroData) return;
    onZeroData();
  }, [isZeroData, onZeroData]);

  return (
    <RetailerNetworkDataLoading isLoading={loading} hasData={!!data} hasError={!!error}>
      {isZeroData ? (
        <RetailerNetworkDataPlaceholder
          title={tobaccoEmptyDataTitle}
          description={tobaccoEmptyDataDescription}
        />
      ) : (
        !!data && (
          <Stack gap={1} mb={8}>
            <RetailerNetworkTobaccoPerformance
              metrics={metrics}
              onMetricClick={onMetricClick}
              performance={data.retailerNetworkTobaccoProgramMetricsOverview.performance}
              previousPerformance={
                data.retailerNetworkTobaccoProgramMetricsOverview.previousPerformance
              }
              totalTransactions={
                data.retailerNetworkTobaccoProgramMetricsOverview.performance.totalTransactions
              }
              prevTotalTransactions={
                data.retailerNetworkTobaccoProgramMetricsOverview.previousPerformance
                  .totalTransactions
              }
            />
            {/* "Offers by Manufacturer" section hidden per OCST-33444 — see note at top of file. */}
          </Stack>
        )
      )}
    </RetailerNetworkDataLoading>
  );
};
